import type { CountryCode } from 'libphonenumber-js';
import { RegisterOptions } from 'react-hook-form';

const MIN_PASSWORD_LENGTH = 8;
const GENERIC_PASSWORD_ERROR_MESSAGE = `Password must be at least ${MIN_PASSWORD_LENGTH} characters long and include one number, one upper case, and one lower case letter.`;
const ZIPCODE_COUNTRY_LENGTH = {
  CA: 6,
  GB: 8,
  US: 5,
};

export const firstNameRules = {
  maxLength: {
    message: 'First name is too long',
    value: 30,
  },
  required: 'First name is required',
} as RegisterOptions;

export const lastNameRules = {
  maxLength: {
    message: 'Last name is too long',
    value: 30,
  },
  required: 'Last name is required',
} as RegisterOptions;

export const emailRules = {
  pattern: {
    message: 'Email is invalid',
    value:
      // From https://stackoverflow.com/questions/46155/how-can-i-validate-an-email-address-in-javascript
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  },
  required: 'Email is required',
} as RegisterOptions;

export const passwordRules = {
  minLength: {
    message: GENERIC_PASSWORD_ERROR_MESSAGE,
    value: MIN_PASSWORD_LENGTH,
  },
  required: 'Password is required',
  validate: {
    complex: (value: string) => {
      const hasNumber = /\d/.test(value);
      const hasUpper = /[A-Z]/.test(value);
      const hasLower = /[a-z]/.test(value);
      const valid = hasNumber && hasUpper && hasLower;
      return valid || GENERIC_PASSWORD_ERROR_MESSAGE;
    },
  },
} as RegisterOptions;

export const phoneNumberRules = (countryCode: CountryCode = 'US') =>
  ({
    maxLength: {
      message: 'Phone number is too long',
      value: 40,
    },
    required: 'Phone number is required',
    validate: {
      complex: async (value: string) => {
        if (!value) return;

        const { isValidNumber } = await import('libphonenumber-js');
        const digitValue = value.replace(/\D/g, '');

        if (isValidNumber(digitValue, countryCode)) return true;
        return 'Phone number is not valid';
      },
    },
  } as RegisterOptions);

export const zipCodeRules = (countryCode: CountryCode = 'US') =>
  ({
    maxLength: {
      message: `Invalid ${countryCode === 'US' ? 'zip' : 'postal'} code. Please enter a valid ${
        ZIPCODE_COUNTRY_LENGTH[countryCode]
      }-${countryCode === 'US' ? 'digit' : 'character'} ${
        countryCode === 'US' ? 'zip' : 'postal'
      } code.`,
      value: ZIPCODE_COUNTRY_LENGTH[countryCode],
    },
    required: `${countryCode === 'US' ? 'Zip' : 'Postal'} code is required`,
    validate: {
      complex: async (value: string) => {
        const { postcodeValidator } = await import('postcode-validator');

        if (postcodeValidator(value, countryCode)) return true;
        return `${countryCode === 'US' ? 'Zip' : 'Postal'} code is not valid`;
      },
    },
  } as RegisterOptions);

export const gradeRules = {
  required: 'Grade is required',
};

const inputValidationRules = {
  emailRules,
  firstNameRules,
  gradeRules,
  lastNameRules,
  passwordRules,
  phoneNumberRules,
  zipCodeRules,
};

export default inputValidationRules;
